import React, { ReactNode, useEffect, useRef } from 'react';
import { Table, ConfigProvider, Flex } from 'antd';
import { ColumnsType, TableProps } from 'antd/es/table';
import { useSearchParams, useLocation } from 'react-router-dom';
import { THEME_VARIABLE } from '@/config';
import { useTableAction } from '@/hooks';
import { PaginationFormat } from '@/hooks/useTableAction';
import { scrollTop } from '@/utils';
import { useTranslation } from 'react-i18next';

export interface ITable {
  [key: string]: any;
}

interface ReusableTableProps<T extends ITable> extends TableProps<T> {
  columns: ColumnsType<T> | any;
  data: ITable[];
  pageSize: number;
  current?: number;
  total?: number;
  onPaginationChange?: (page: number, pageSize?: number) => void;
  rowSelectionEnabled?: boolean;
  header?: ReactNode;
  selectedRowKeys?: string[];
  setSelectedRowKeys?: (values: string[]) => void;
  hidePagination?: boolean;
  loading?: boolean;
  customClassName?: string;
  fixed?: 'left' | 'right' | undefined;
  paginationFormat?: PaginationFormat;
}

function TableAction<T extends ITable>({
  columns,
  data,
  pageSize,
  current,
  total,
  onPaginationChange,
  rowSelectionEnabled = false,
  header,
  selectedRowKeys,
  setSelectedRowKeys,
  hidePagination = false,
  loading = false,
  customClassName,
  paginationFormat,
  ...props
}: ReusableTableProps<T>) {
  const { customTitle } = useTableAction();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { t } = useTranslation();

  // Capture the pathname at mount time to detect navigation away
  const mountPathnameRef = useRef(location.pathname);

  const currentPage = searchParams.get('page') ? parseInt(searchParams.get('page')!, 10) : current;

  useEffect(() => {
    // Skip if navigated away from the route that mounted this component
    if (location.pathname !== mountPathnameRef.current) return;

    const pageParam = searchParams.get('page');

    if (pageParam && onPaginationChange) {
      const pageNumber = parseInt(pageParam, 10);
      if (!isNaN(pageNumber) && pageNumber > 0 && pageNumber !== current) {
        onPaginationChange(pageNumber, pageSize);
      }
    }
  }, [searchParams, onPaginationChange, pageSize, location.pathname]);

  const rowSelection = rowSelectionEnabled
    ? {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => {
          setSelectedRowKeys?.(newSelectedRowKeys.map((key) => String(key)));
        },
      }
    : undefined;

  const existHeader = header ? () => header : undefined;

  const handleChangePage = (page: number, pageSize: number) => {
    // Skip if navigated away from the route that mounted this component
    if (location.pathname !== mountPathnameRef.current) return;

    scrollTop();
    onPaginationChange?.(page, pageSize);

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      newParams.set('page', page.toString());
      return newParams;
    });
  };

  const dataList = Array.isArray(data) ? data : Object.values(data);

  return (
    <ConfigProvider
      theme={{
        components: {
          Checkbox: {
            colorPrimary: THEME_VARIABLE.customTunaPrimary,
            colorPrimaryHover: THEME_VARIABLE.customTunaPrimary,
            colorBorder: THEME_VARIABLE.customTunaPrimary,
          },
          Table: {
            headerBg: THEME_VARIABLE.colorWhite,
            headerSplitColor: 'transparent',
          },
        },
      }}
    >
      <Table<T>
        {...props}
        rowSelection={rowSelection}
        columns={columns}
        dataSource={dataList as any}
        title={existHeader}
        loading={loading}
        className={['redesign-table', customClassName, props.className].filter(Boolean).join(' ')}
        pagination={
          hidePagination || !total
            ? false
            : {
                pageSize,
                current: currentPage,
                total,
                showSizeChanger: false,
                showTotal: (total, range) => customTitle(total, range, paginationFormat),
                onChange: handleChangePage,
              }
        }
        // footer={() => <></>}
        scroll={{ x: 'max-content' }}
        locale={{
          triggerDesc: t('table.sortDesc'),
          triggerAsc: t('table.sortAsc'),
          cancelSort: t('table.cancelSort'),
          emptyText: loading ? <div className='h-16' /> : t('table.noData'),
        }}
      />
    </ConfigProvider>
  );
}

export default TableAction;
